import moment from "moment";
import { API, Auth } from "aws-amplify";
import awsmobile, { getAPIName } from '../config';

interface AnyObject {
  [key: string]: any;
}

export function filterObjectKeys(obj: AnyObject, filterkeys: string[], remove: boolean = false): AnyObject {
  return Object.keys(obj)
    .filter(key =>  remove ? !filterkeys.includes(key) : filterkeys.includes(key))
    .reduce((newObj: AnyObject, key: string) => {
      newObj[key] = obj[key];
      return newObj;
    }, {});
}

//Trims whitespace from all string values in an object.
export function trimObjectValues(obj: AnyObject): AnyObject {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => 
      [key, typeof value === 'string' ? value.trim() : value]
    )
  );
}

const validateEmail = (email: string) => {
  return email.match(
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  );
};

export const validateNumber = (number: string) => {
  return number.match(
    /^(?:[1-9]\d*|0)?(?:\.\d+)?$/
  );
};

export const convertUTCToLocal = (utcDateTime: any) => {
  return moment.utc(utcDateTime).local().format('YYYY-MM-DDTHH:mm');
};

export const convertLocalToUTC = (localDateTime:any) => {
  return moment(localDateTime).utc().format();
};

export const generateMailHref = (
  status?: string,
  name?: string,
  emailaddress?: string,
  contractnumber?: string,
  bucketpath?: string,
  dealershipname?: string,
  isSubscription?: boolean,
  paymentlink?: string
): string | null => {
    status = status?.toLowerCase();
    if (status !== 'missing_cc_token' && status !== 'declined') return null;
    const subject =
      status === 'missing_cc_token'
        ? `Action Required: Review Toll Charges from Loaner/Rental Vehicle - ${dealershipname || ''}`
        : `Action Required: Declined Payment for Toll Charges - ${dealershipname || ''}`
    const body = status == 'declined' ? `Dear ${name},%0A%0AWe attempted to process a payment for toll charges related to your use of a loaner/rental vehicle under rental agreement #${contractnumber || ''} from ${dealershipname || ''}, but the credit card on file was declined.%0A%0APlease review the charges and submit payment using the "Pay Online" button in the agreement below.%0A%0Ahttps://tollaid-emailtemplates.s3.us-east-2.amazonaws.com/prod/${bucketpath || ''}%0A%0Aor click here to make payment directly:%0A ${paymentlink || ''}%0A%0AIf you have any questions or need assistance, feel free to contact us.%0A%0AThank you,%0A%0A` :
        isSubscription ? `Dear ${name},%0A%0AWe noticed that we no longer have a credit card on file in our system. The charges in the link below are related to tolls accrued while you were using a loaner/rental vehicle under rental agreement #${contractnumber || ''} from ${dealershipname || ''}.%0A%0APlease review the charges :%0Ahttps://tollaid-emailtemplates.s3.us-east-2.amazonaws.com/prod/${bucketpath || ''}%0A%0Ayou can pay full amount here:%0A ${paymentlink || ''}%0A%0Aor you can visit https://mytollreceipt.com/fleet/ for other payment options. %0A%0AIf you have any questions or need assistance, feel free to reach out.%0A%0AThank you,%0A%0A` :
        `Dear ${name},%0A%0AWe noticed that we no longer have a credit card on file in our system. The charges in the link below are related to tolls accrued while you were using a loaner/rental vehicle under rental agreement #${contractnumber || ''} from ${dealershipname || ''}.%0A%0APlease review the charges and submit payment using the "Pay Online" button in the agreement below.%0A%0Ahttps://tollaid-emailtemplates.s3.us-east-2.amazonaws.com/prod/${bucketpath || ''}%0A%0Aor click here to make payment directly:%0A ${paymentlink || ''}%0A%0AIf you have any questions or need assistance, feel free to reach out.%0A%0AThank you,%0A%0A`
    return `mailto:${emailaddress || ''}?subject=${encodeURIComponent(subject)}&body=${body}`;
  };

export const generateEscalationMailHref = (
  name?: string,
  emailaddress?: string,
  dealershipname?: string,
  bucketpath?: string,
  paymentlink?: string,
  amount?: string,
  daysDiff?: number,
): string => {
  if (!emailaddress) return '';
  const subject = "Final Notice – Action Required on Unpaid Toll Charges";
  const body = `Dear ${name || ''},%0A%0AThis is a final notice regarding unpaid toll charges from your use of a loaner vehicle at ${dealershipname || ''}. TollAid, the third-party company that manages the toll transactions for our store, has forwarded these unpaid charges to us for resolution.%0A%0AThe balance of $${amount || ''} has been outstanding for over ${daysDiff || 60} days and must be paid within 15 days to avoid referral to a collection agency. Unresolved toll charges may also affect your eligibility for future loaner vehicles.%0A%0APlease review the charges and submit payment using the "Pay Online" button in the agreement below.%0A%0Ahttps://tollaid-emailtemplates.s3.us-east-2.amazonaws.com/prod/${bucketpath || ''}%0A%0Aor click here to make payment directly:%0A ${paymentlink || ''}%0A%0AIf you have any questions or need assistance, feel free to reach out.%0A%0AThank you for your immediate attention.%0A%0A`;
  return `mailto:${emailaddress}?subject=${encodeURIComponent(subject)}&body=${body}`;
};

export const getCurrentUser = async () => {
  try {
    const currentSessionData = await Auth.currentAuthenticatedUser();
    const username = currentSessionData.attributes?.name || currentSessionData.attributes?.email || null;
    const email = currentSessionData.attributes?.email || null;
    return {
      username,
      email,
      fullData: currentSessionData
    };
  } catch (error) {
    console.error('Error fetching current user:', error);
    return {
      username: null,
      email: null,
      fullData: null
    };
  }
};

export const getLocationInfoFromUser = async (givenName: string, familyName: string) => {
  try {
    if (givenName === 'Admin') return 'Admin';
    const apiName = getAPIName();
    const path = givenName === 'Group' ? '/dealershipgroups' : '/dealerships';
    const res = await API.get(apiName, `${path}?id=${familyName}`, {});
    return res?.body?.[0]?.name || null;
  } catch (error) {
    console.error('Error fetching user location info:', error);
    return null;
  }
};

export const uploadMultipart = async (path: string, formData: FormData, method: 'POST' | 'PUT' | 'PATCH' = 'POST') => {
  const token = (await Auth.currentSession()).getAccessToken().getJwtToken();
  const endpoint = awsmobile.aws_cloud_logic_custom.find(x => x.name === getAPIName())?.endpoint;
  const res = await fetch(`${endpoint}${path}`, {
    method,
    headers: { access_token: token },
    body: formData
  });
  if (!res.ok) throw await res.json();
  return res.json();
};
export default validateEmail
